import type {
  InputData,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import {
  InputGroup,
  KeyValueCustomSelect,
  Text,
  useInputGroupState,
} from "@smartsuite/react-ui/lib";
import type React from "react";
import { useEffect, useState } from "react";
import { getSVG } from "../../../../utils/get-svg";
import { isRequired } from "../../Forms/InputForm/utils";
import * as S from "./styles";
import type { SelectOption } from "../../../../domain/select-option";
import { t } from "i18next";
import { SanitizedHTML } from "../../../../utils/sanitizedHtml";
import { translateIfPresent } from "../../../../locales/i18n";

export const SelectEditor: React.FC<{
  description: InputDescription;
  input: InputData;
  onChange: (input: InputData) => void;
  hasError?: boolean;
}> = ({ description, input, onChange, hasError }) => {
  const inputValue = input.value?.values?.[0].value;
  const [value, setValue] = useState<string>("");

  const { state } = useInputGroupState();
  let options: Array<{ label: string; value: string }> = [];
  if (description.editor?.editor.oneofKind === "select") {
    options = description.editor?.editor.select.options.map((option) => ({
      label: option.label,
      value: option.value,
      icon: option.icon,
    }));
  }

  useEffect(() => {
    if (inputValue?.oneofKind === "string") {
      setValue(inputValue.string);
    }
  }, [inputValue]);

  if (description.editor?.editor.oneofKind !== "select") return null;
  if (inputValue?.oneofKind !== "string") return null;

  const renderOption = (option: SelectOption): React.ReactNode => {
    const Icon = getSVG(option.icon);
    return (
      <S.Option>
        {Icon && <Icon />}
        <Text wrapMode="ellipsis" weight="medium" color="currentColor">
          {option.label}
        </Text>
      </S.Option>
    );
  };

  return (
    <S.ReactInputFieldContainer>
      {description.label !== "" && (
        <>
          <S.InputLabel>
            {t(`caption.${description.label}`, description.label)}
          </S.InputLabel>
          {isRequired(description.display) && (
            <Text size="s" weight="semibold" color="error">
              *
            </Text>
          )}
        </>
      )}
      {description.subLabel !== "" && (
        <S.InputSubLabel>
          <SanitizedHTML
            html={translateIfPresent(
              `caption.${description.subLabel}`,
              description.subLabel,
            )}
            options={{
              allowedTags: ["span", "p", "b", "a"],
              allowedAttributes: {
                a: ["href", "target"],
                span: ["style"],
              },
            }}
          />
        </S.InputSubLabel>
      )}
      <InputGroup state={state}>
        <KeyValueCustomSelect
          value={value}
          disabled={false}
          placeholderArrowStyle="filled-dark"
          options={options}
          renderOption={renderOption}
          className={hasError ? "error" : "thin-border"}
          onChange={(value) => {
            onChange({
              ...input,
              value: {
                values: [
                  {
                    value: {
                      oneofKind: "string",
                      string: String(value ?? ""),
                    },
                    label: "",
                    icon: "",
                  },
                ],
                metadata: {},
              },
            });
          }}
        />
      </InputGroup>
    </S.ReactInputFieldContainer>
  );
};
